import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  EMPLOYER_GATE_ANALYSIS_FIRST_REVIEW_TARGET,
  findingTourTarget,
} from '../../lib/employerReview/checkpointTourCopy';
import { scrollToEmployerReviewTarget } from '../../lib/employerReview/employerReviewNavigate';
import {
  findingVisualTheme,
  resolveFindingAccent,
  resolveFindingLinkColor,
} from '../../lib/employerReview/findingVisualTheme';
import type { AdjudicatorFindingBrief } from '../../lib/employerReview/resolveAdjudicatorFindingBrief';
import { FINDING_BRIEF_SECTION_LABEL } from './findingBriefSectionStyles';
import { FindingBriefSection } from './FindingBriefSection';
import { ReviewEvidenceButton } from './guidedTour/ReviewEvidenceButton';
import { useEmployerEvidenceTourOptional } from './guidedTour/EmployerEvidenceTourContext';

function sourceLabel(source: AdjudicatorFindingBrief['source']): string | null {
  if (source === 'ai_role_sector') return 'AI role & sector pre-read';
  if (source === 'ai_linkage') return 'AI payroll linkage pre-read';
  return null;
}

export function AdjudicatorFindingBriefPanel({
  brief,
  index: _index,
  embedded = false,
}: {
  brief: AdjudicatorFindingBrief;
  index: number;
  embedded?: boolean;
}) {
  const tour = useEmployerEvidenceTourOptional();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const visual = findingVisualTheme(brief.findingId, brief.severity, isLight);
  const aiSource = sourceLabel(brief.source);
  const advisoryColor = brief.advisoryLabel?.includes('PASS') ? 'success' : 'warning';
  const findingAccent = resolveFindingAccent(brief.findingId, brief.severity, theme, isLight);
  const linkAccent = resolveFindingLinkColor(brief.findingId, brief.severity, theme, isLight);

  const followUpPoints =
    brief.followUpPoints?.length
      ? brief.followUpPoints
      : brief.followUpEvidence.trim()
        ? [brief.followUpEvidence.trim()]
        : [];

  const onReviewEvidence = () => {
    if (tour) {
      tour.startTourForFinding(brief.findingId);
      return;
    }
    scrollToEmployerReviewTarget(EMPLOYER_GATE_ANALYSIS_FIRST_REVIEW_TARGET);
  };

  const body = (
    <Stack spacing={1.15} sx={{ px: embedded ? 1.5 : 1.25, py: embedded ? 1.25 : 1.1 }}>
      {embedded && (aiSource || brief.advisoryLabel) ? (
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {aiSource ? (
            <Chip
              size="small"
              icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: '14px !important' }} />}
              label={aiSource}
              variant="outlined"
              sx={{ height: 22, fontWeight: 600 }}
            />
          ) : null}
          {brief.advisoryLabel ? (
            <Chip
              size="small"
              label={brief.advisoryLabel}
              color={advisoryColor}
              variant="outlined"
              sx={{ height: 22, fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      ) : null}

      {brief.caseSummary.trim() || brief.caseSummaryPoints?.length ? (
        <FindingBriefSection
          variant="summary"
          accent={findingAccent}
          label={FINDING_BRIEF_SECTION_LABEL.summary}
          lead={brief.caseSummary}
          points={brief.caseSummaryPoints}
        />
      ) : null}

      {brief.reviewMeasures.trim() || brief.reviewMeasuresPoints?.length ? (
        <FindingBriefSection
          variant="measures"
          accent={findingAccent}
          label={FINDING_BRIEF_SECTION_LABEL.measures}
          lead={brief.reviewMeasures}
          points={brief.reviewMeasuresPoints}
        />
      ) : null}

      {followUpPoints.length ? (
        <FindingBriefSection
          variant="followup"
          accent={findingAccent}
          label={FINDING_BRIEF_SECTION_LABEL.followup}
          points={followUpPoints}
        />
      ) : null}

      {brief.footerNote ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            lineHeight: 1.4,
            display: 'block',
            pt: 0.25,
            borderTop: '1px dashed',
            borderColor: 'divider',
            fontSize: '0.75rem',
          }}
        >
          {brief.footerNote}
        </Typography>
      ) : null}

      <Box sx={{ pt: 0.35 }} data-tour-target={`finding-brief-review-evidence-${brief.findingId}`}>
        <ReviewEvidenceButton compact accent={linkAccent} onClick={onReviewEvidence} />
      </Box>
    </Stack>
  );

  if (embedded) {
    return (
      <Box data-tour-target={findingTourTarget(brief.findingId)}>
        {body}
      </Box>
    );
  }

  return (
    <Box
      data-tour-target={findingTourTarget(brief.findingId)}
      sx={{
        border: '2px solid',
        borderColor: visual?.borderColor ?? 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: visual
          ? visual.accentSoft
          : isLight
            ? alpha(theme.palette.common.black, 0.015)
            : alpha(theme.palette.common.white, 0.03),
      }}
    >
      {body}
    </Box>
  );
}
